// Pure logic shared by every Formik wrapper in this folder: id derivation
// + error/touched lookup. The label-rendering branch lives in
// `formik-field-label.component.tsx` so this hook stays JSX-free and
// trivially unit-testable.
//
// Why id is `id || [fid, field.name].filter(Boolean).join('-')`:
// `fid` is a form-level prefix shared by every field on a form, so on its
// own it would collide if two fields share the same form. Suffixing
// `field.name` (which is unique within a Formik form) yields a DOM id that
// is stable, predictable, and label-targetable via `htmlFor`. Previously
// formik-textarea / formik-datepicker / formik-timepicker used `id || fid`
// alone — that drift was unified during the `useFormikField` extraction.

interface FormikFieldLike {
  name: string;
}

interface FormikFormLike {
  errors: Record<string, any>;
  touched: Record<string, any>;
}

// Shared by every Formik wrapper in this folder. Each wrapper's prop
// interface `extends FormikFieldCommonProps` and adds only its
// variant-specific props (e.g. `type` for input, `options` for select).
// Kept `any` for `field` / `form` / `label` to match the wrappers' historical
// contracts; tightening would mean threading Formik / IntlMsg generics
// through every wrapper, which is out of scope here.
export interface FormikFieldCommonProps {
  id?: string;
  fid?: string;
  field: any;
  form: any;
  label?: any;
}

interface UseFormikFieldArgs {
  field: FormikFieldLike;
  form: FormikFormLike;
  fid?: string;
  id?: string;
}

interface UseFormikFieldResult {
  id: string;
  error: any;
  touched: any;
}

export const useFormikField = ({ field, form, fid, id }: UseFormikFieldArgs): UseFormikFieldResult => {
  const resolvedId = id || [fid, field.name].filter(Boolean).join('-');
  return {
    id: resolvedId,
    error: form.errors[field.name],
    touched: form.touched[field.name],
  };
};
